import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { validateApiResponse } from '../../../services';
import { submitImport } from '../../../services/importService.js';
import { buildSubmitPayload } from '../utils/buildSubmitPayload.js';
import { usePeopleAccessPaths } from './usePeopleAccessPaths.js';

/**
 * Adds the reviewed people (step 4). No confirmation dialog: on success a toast reports the
 * counts and the Members list opens filtered to the batch; on failure the wizard keeps its
 * state and `failed` is true so the step can show a banner.
 *
 * @param {object} state Wizard state.
 * @returns {{ submit: () => Promise<void>, submitting: boolean, failed: boolean }}
 */
export function useSubmitImport(state) {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const navigate = useNavigate();
  const { membersPath } = usePeopleAccessPaths();
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSubmitting(true);
    setFailed(false);
    try {
      const payload = buildSubmitPayload(state);
      const { body } = validateApiResponse(await submitImport(payload));
      const withAccess = payload.new_members.filter((m) => m.role_ids.length > 0 || m.user_type === 'admin').length;
      toast.success({
        title: t('import.check.result.title', { count: body.added, updated: body.updated }),
        message: t('import.check.result.message', {
          withAccess,
          noAccess: body.added - withAccess,
          invites: body.invited > 0 ? t('import.check.result.invitedSome', { count: body.invited }) : t('import.check.result.invitedNone'),
        }),
      });
      if (body.failed.length > 0) {
        toast.warning({
          title: t('import.check.result.partialTitle'),
          message: t('import.check.result.partialMessage', { count: body.failed.length }),
        });
      }
      // With no new person there is no batch to filter the Members list by.
      const importBatch =
        body.added > 0
          ? {
              id: body.batch_id,
              date: new Date().toISOString(),
              file: state.file.name,
              notInvited: payload.new_members.filter((m) => !m.invite).length,
            }
          : undefined;
      navigate(membersPath, importBatch ? { state: { importBatch } } : undefined);
    } catch {
      // The banner of the wizard stays on screen; the toast tells it where the user is looking.
      toast.error({ title: t('import.check.result.failedTitle'), message: t('import.check.result.failedMessage') });
      if (mountedRef.current) setFailed(true);
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  }, [state, toast, t, navigate, membersPath]);

  return { submit, submitting, failed };
}
